import { Link } from 'react-router-dom'
import { data, dataHoraPorExtenso } from '../../../lib/datas'
import type { ResumoChamado } from '../../../types/chamado'
import { BarraDeSla } from '../BarraDeSla'
import { EtiquetaDeOrigemDaDemanda, EtiquetaDeProcedencia, EtiquetaDeSla } from '../etiquetas'
import { Fechamento, GmudVinculada, Julgamento, ValorOuVazio } from './marcadores'

/**
 * O mesmo chamado, no telefone.
 *
 * Cartão e não tabela rolando de lado: fiscal de contrato confere chamado do celular, e uma tabela
 * de oito colunas em 360px vira uma faixa que se arrasta com o dedo — quem lê a primeira coluna
 * nunca chega no prazo, que é a informação que ele foi buscar.
 *
 * O cartão inteiro é UM link, e não uma caixa clicável com um link dentro: assim o alvo de toque é
 * o cartão todo, o leitor de tela anuncia um destino só e o "abrir em nova aba" continua
 * funcionando. Por isso nada aqui dentro pode virar botão.
 *
 * A procedência aparece aqui, e não só na tabela larga: é no telefone que ela é mais fácil de
 * perder e mais cara de perder.
 *
 * @param props a linha da listagem
 * @returns o cartão
 */
export function CartaoDeChamado({ chamado }: { chamado: ResumoChamado }) {
  return (
    <li>
      <Link
        to={`/chamados/${chamado.id}`}
        className={
          'block rounded-xl border border-slate-200 bg-white p-4 shadow-cartao transition-colors ' +
          'hover:border-niteroi-200 hover:bg-niteroi-50 focus-visible:outline focus-visible:outline-2 ' +
          'focus-visible:outline-offset-2 focus-visible:outline-niteroi-600'
        }
      >
        <div className="flex items-center justify-between gap-2">
          <span className="font-semibold tabular-nums text-niteroi-700">{chamado.idExterno}</span>
          <time
            dateTime={chamado.abertoEm}
            title={dataHoraPorExtenso(chamado.abertoEm)}
            className="text-xs tabular-nums text-slate-500"
          >
            {data(chamado.abertoEm)}
          </time>
        </div>

        <p className="mt-2 text-sm font-medium text-slate-900">{chamado.titulo}</p>
        <GmudVinculada chamado={chamado} />

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <EtiquetaDeSla situacao={chamado.situacaoSla} rotulo={chamado.situacaoSlaRotulo} />
          <BarraDeSla
            abertoEm={chamado.abertoEm}
            prazoSla={chamado.prazoSla}
            fechadoEm={chamado.fechadoEm}
            situacao={chamado.situacaoSla}
          />
          <EtiquetaDeOrigemDaDemanda origem={chamado.origemDemanda} />
          <EtiquetaDeProcedencia origem={chamado.origem} />
          {/* Também no telefone: é lá que o fiscal confere correndo, e uma marca que só existe na
              tabela é uma marca que metade dos acessos não vê. */}
          <Julgamento julgado={chamado.julgado} />
        </div>

        {/* `<dl>` e não linhas soltas: sem a marcação, "Entidade" e "SMA" viram dois textos sem
            relação para quem ouve a tela. */}
        <dl className="mt-3 grid grid-cols-[auto,1fr] gap-x-3 gap-y-1 text-sm">
          <dt className="text-slate-500">Entidade</dt>
          <dd className="min-w-0 text-slate-800">
            <ValorOuVazio texto={chamado.entidade} />
          </dd>
          <dt className="text-slate-500">Grupo</dt>
          <dd className="min-w-0 text-slate-800">
            <ValorOuVazio texto={chamado.grupo} />
          </dd>
          <dt className="text-slate-500">Fechado em</dt>
          <dd className="min-w-0 tabular-nums text-slate-800">
            <Fechamento fechadoEm={chamado.fechadoEm} />
          </dd>
        </dl>
      </Link>
    </li>
  )
}
